<template>
  <div class="common-datepicker" :class="[ { 'mobile w-100 h-100': isPhone }, datePickerClass ]">
    <HotelDatePicker
      :startingDateValue="startingDateValue"
      :endingDateValue="endingDateValue"
      :startDate="startDate"
      :endDate="resolvedEndDate"
      :format="format"
      :gridStyle="gridStyle"
      :hoveringTooltip="hoveringTooltip"
      :i18n="i18n"
      :firstDayOfWeek="firstDayOfWeek"
      :closeDatepickerOnClickOutside="closeDatepickerOnClickOutside"
      :alwaysVisible="alwaysVisible"
      :disabledDates="normalizedDisabledDates"
      :enableCheckout="enableCheckout"
      :halfDay="halfDay"
      ref="hotelDatePicker"
      @periodSelected="handlePeriodSelected"
      @opened="handleOpened"
      @closed="handleClosed"
      @toggle="handleToggle"
    />
    
    <button
      v-show="(isDatepickerOpen || alwaysVisible) && !hideClose"
      class="close-cross-icon"
      @click="closeDatepicker()"
      aria-label="Fermer le calendrier"
    >
      <i class="fas fa-times" v-if="!isPhone"></i>
      <span v-if="isPhone" class="raleway-semi-p14">Fermer</span>
    </button>
    
    
    <button
      v-if="isDatepickerOpen && showCloseButton && isPhone"
      class="close-date-picker"
      @click="handleCloseButtonClick"
      @click.stop="handleCloseButtonClick"
    >
      {{ closeButtonText }}
    </button>

    <button
      v-if="(isDatepickerOpen || alwaysVisible)"
      class="clear-dates-button raleway-semi-p13 text-underline"
      @click="clearDates"
      aria-label="Effacer les dates"
      ref="clearDatesBtn"
    >
      Effacer les dates
    </button>
  </div>
</template>

<script>
import HotelDatePicker from "vue-hotel-datepicker";

// Extended HotelDatePicker with custom toggleDatepicker method
const ExtendedHotelDatePicker = {
  extends: HotelDatePicker,
  methods: {
    createHalfDayDates(_baseHalfDayDates) {
      let sortedDates = []
      const checkIncheckOutHalfDay = {}
      const baseHalfDayDates = [..._baseHalfDayDates]

      baseHalfDayDates.sort((a, b) => {
        const aa = a.split('/').reverse().join()
        const bb = b.split('/').reverse().join()
        return aa < bb ? -1 : aa > bb ? 1 : 0
      })

      sortedDates = [...baseHalfDayDates]

      if (this.halfDay) {
        for (let i = 0; i < baseHalfDayDates.length; i++) {
          const current = baseHalfDayDates[i]
          const prev = baseHalfDayDates[i - 1]
          const next = baseHalfDayDates[i + 1]

          const isFirst =
            !prev || this.getDayDiff(prev, current) > 1

          const isLast =
            !next || this.getDayDiff(current, next) > 1

          if (isFirst) {
            checkIncheckOutHalfDay[current] = { checkIn: true }
          }

          if (isLast) {
            checkIncheckOutHalfDay[current] = {
              ...checkIncheckOutHalfDay[current],
              checkOut: true,
            }
          }
        }

        const halfDays = Object.keys(checkIncheckOutHalfDay)
        sortedDates = sortedDates.filter(date => !halfDays.includes(date))
      }

      sortedDates = sortedDates.map(date => new Date(date))
      this.sortedDisabledDates = sortedDates.sort((a, b) => a - b)
      this.checkIncheckOutHalfDay = checkIncheckOutHalfDay

      this.$emit(
        "handle-checkin-checkout-half-day",
        this.checkIncheckOutHalfDay
      )
    },
    toggleDatepicker() {
      // Call the original library method logic
      this[this.isOpen ? 'hideDatepicker' : 'showDatepicker']();
      // Emit custom event to parent for state tracking
      this.$emit('toggle', this.isOpen);
    },
    handleClickOutside(event) {
      const ignoreClickOnMeElement = this.$refs[`DatePicker-${this.hash}`]

      if (ignoreClickOnMeElement) {
        const isClickInsideElement = ignoreClickOnMeElement.contains(event.target)

        if (!isClickInsideElement) {
          this.hideDatepicker()
          this.$emit('toggle', false);
        }
      }
    },
    // Override generateInitialMonths to create all months for mobile
    generateInitialMonths() {
      this.months = []

      if (!this.isDesktop) {
        // On mobile, generate all months from start date to end date
        const startDate = new Date(this.startDate);
        const endDate = this.endDate !== Infinity
          ? new Date(this.endDate)
          : new Date(startDate.getFullYear() + 1, 11, 31);
        
        let currentDate = new Date(startDate);
        
        // Generate months from start to end
        while (currentDate <= endDate) {
          this.createMonth(new Date(currentDate));
          currentDate = this.getNextMonth(currentDate);
        }
        
        // Set active month index based on checkIn date if available
        if (this.checkIn) {
          const checkInMonth = new Date(this.checkIn);
          this.activeMonthIndex = this.getMonthDiff(startDate, checkInMonth);
        } else {
          this.activeMonthIndex = 0;
        }
      } else {
        // On desktop, use original logic
        if (
          this.checkIn &&
          (this.getMonthDiff(this.getNextMonth(new Date(this.startDate)), this.checkIn) > 0 ||
            this.getMonthDiff(this.startDate, this.checkIn) > 0)
        ) {
          this.createMonth(new Date(this.startDate))
          const count = this.getMonthDiff(this.startDate, this.checkIn)
          const monthCount = this.showSingleMonth ? count - 1 : count
          let nextMonth = new Date(this.startDate)

          for (let i = 0; i <= monthCount; i++) {
            const tempNextMonth = this.getNextMonth(nextMonth)

            this.createMonth(tempNextMonth)
            nextMonth = tempNextMonth
          }

          if (this.checkOut && this.getMonthDiff(this.checkIn, this.checkOut) > 0) {
            this.createMonth(this.getNextMonth(nextMonth))
            this.activeMonthIndex = 1
          }

          this.activeMonthIndex += count
        } else {
          this.createMonth(new Date(this.startDate))

          if (!this.showSingleMonth) {
            this.createMonth(this.getNextMonth(new Date(this.startDate)))
          }
        }
      }
    },
    // New method to scroll to selected dates on mobile
    scrollToSelectedDates() {
      if (!this.isDesktop) {
        // Find the datepicker container element
        const datepickerContainer = this.$el?.querySelector('.vhd__datepicker__months') ||
                                   this.$el?.querySelector('.datepicker-months') ||
                                   this.$el?.querySelector('[class*="month"]')?.parentElement;
        
        if (!datepickerContainer) return;

        let targetScrollTop = 0;

        // If we have checkIn date, scroll to that month
        if (this.checkIn) {
          const checkInMonthIndex = this.getMonthDiff(new Date(this.startDate), this.checkIn);
          const monthElements = datepickerContainer.querySelectorAll('.vhd__datepicker__month, [class*="month"]');
          
          if (monthElements && monthElements[checkInMonthIndex]) {
            const monthHeight = monthElements[0].offsetHeight || 400; // fallback height
            targetScrollTop = checkInMonthIndex * monthHeight;
          }
        }
        // If we have checkOut date and no checkIn, scroll to checkOut month
        else if (this.checkOut) {
          const checkOutMonthIndex = this.getMonthDiff(new Date(this.startDate), this.checkOut);
          const monthElements = datepickerContainer.querySelectorAll('.vhd__datepicker__month, [class*="month"]');
          
          if (monthElements && monthElements[checkOutMonthIndex]) {
            const monthHeight = monthElements[0].offsetHeight || 400; // fallback height
            targetScrollTop = checkOutMonthIndex * monthHeight;
          }
        }

        // Smooth scroll to the target position
        if (targetScrollTop > 0 && datepickerContainer.scrollTo) {
          datepickerContainer.scrollTo({
            top: targetScrollTop
          });
        } else if (targetScrollTop > 0) {
          // Fallback for browsers that don't support smooth scrolling
          datepickerContainer.scrollTop = targetScrollTop;
        }
      }
    },
  },
  computed: {
    // Override paginateMonths to show all months on mobile
    paginateMonths() {
      const parentComponent = this.$parent;
      const isPhone = (parentComponent && parentComponent.isPhone) || false;
      if (isPhone) {
        // On mobile, return all months for scrolling
        return this.months;
      }
      
      // On desktop, use original pagination logic
      const months = [this.months[this.activeMonthIndex]];
      if (!(this.showSingleMonth || (this.alwaysVisible && !this.isDesktop))) {
        months.push(this.months[this.activeMonthIndex + 1]);
      }
      return months;
    },
  }
};

export default {
  name: "CommonDatePicker",
  components: {
    HotelDatePicker: ExtendedHotelDatePicker,
  },
  props: {
    hideClose: {
      type: Boolean,
      default: false,
    },
    // Date values
    startingDateValue: {
      type: [Date, String],
      default: null,
    },
    endingDateValue: {
      type: [Date, String],
      default: null,
    },
    startDate: {
      type: Date,
      default: () => new Date(),
    },
    endDate: {
      type: Date,
      default: () => new Date(new Date().getFullYear() + 1, 11, 31),
    },
    // Array of "YYYY-MM-DD" strings or Date objects that should be visually
    // disabled and non-selectable in the calendar.
    disabledDates: {
      type: Array,
      default: () => [],
    },
    
    // Display options
    format: {
      type: String,
      default: 'DD/MM/YYYY',
    },
    gridStyle: {
      type: Boolean,
      default: false,
    },
    hoveringTooltip: {
      type: Boolean,
      default: false,
    },
    firstDayOfWeek: {
      type: Number,
      default: 1,
    },
    
    // Behavior options
    closeDatepickerOnClickOutside: {
      type: Boolean,
      default: true,
    },
    alwaysVisible: {
      type: Boolean,
      default: false,
    },
    autoHide: {
      type: Boolean,
      default: true,
    },
    enableCheckout: {
      type: Boolean,
      default: true,
    },
    halfDay: {
      type: Boolean,
      default: true,
    },
    // Mobile specific options
    showCloseButton: {
      type: Boolean,
      default: false,
    },
    closeButtonText: {
      type: String,
      default: 'SUIVANT',
    },
    
    datePickerClass: {
      type: String,
      default: '',
    },
  },
  
  data() {
    return {
      isDatepickerOpen: false,
      scrollPosition: 0,
      i18n: {
        night: "Nuit",
        nights: "Nuits",
        week: "semaine",
        weeks: "semaines",
        "day-names": ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"],
        "check-in": "Arrivée",
        "check-out": "Départ",
        "month-names": [
          "Janvier",
          "Février",
          "Mars",
          "Avril",
          "Mai",
          "Juin",
          "Juillet",
          "Août",
          "Septembre",
          "Octobre",
          "Novembre",
          "Décembre",
        ],
        tooltip: {
          halfDayCheckIn: "Available CheckIn",
          halfDayCheckOut: "Available CheckOut",
          saturdayToSaturday: "Only Saturday to Saturday",
          sundayToSunday: "Only Sunday to Sunday",
          minimumRequiredPeriod: "%{minNightInPeriod} %{night} minimum.",
        },
      }
    };
  },
  
  computed: {
    isPhone() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry/i.test(navigator.userAgent);
    },
    resolvedEndDate() {
      const start = new Date(this.startDate || new Date());
      const maxLimit = new Date(start.getFullYear() + 1, 11, 31);

      if (!this.endDate || this.endDate === Infinity) {
        return maxLimit;
      }

      const providedEnd = new Date(this.endDate);
      if (Number.isNaN(providedEnd.getTime())) {
        return maxLimit;
      }

      return providedEnd > maxLimit ? maxLimit : providedEnd;
    },

    // Normalises the disabledDates prop into an array of Date objects at
    // midnight UTC so vue-hotel-datepicker can compare them correctly.
    // Accepts "YYYY-MM-DD" strings or Date objects; silently skips invalid values.
    normalizedDisabledDates() {
      if (!Array.isArray(this.disabledDates) || this.disabledDates.length === 0) {
        return [];
      }
      return this.disabledDates;
    },
  },
  
  watch: {
    isDatepickerOpen: {
      deep: true,
      handler(v) {
        if (this.isPhone) {
          if (v) {
              this.disableBodyScroll();
              // On mobile, scroll to selected dates after datepicker is shown
              if (this.isPhone) {
                this.$nextTick(() => {
                  this.$refs.hotelDatePicker.scrollToSelectedDates();
                });
              }
          } else {
            this.enableBodyScroll();
          }
        }
        // Move clear button inside datepicker on desktop
        if (!this.isPhone) {
          this.$nextTick(() => {
            this.moveButtonToDatepicker();
          });
        }
      }
    },
  },
  
  methods: {
    handlePeriodSelected(event, start, end) {
      this.$emit('periodSelected', event, start, end);
      if (this.showCloseButton && this.isPhone) {
        this.$nextTick(() => {
          if (this.isPhone) {
            this.showDatepicker(); // Keep datepicker open
          }
        });
      } else {
        if (this.autoHide) {
          this.isDatepickerOpen = false;
          this.closeDatepicker(false);
        }
      }
    },
    
    handleOpened() {
      this.isDatepickerOpen = true;
      this.$emit('opened');
    },

    handleClosed() {
      this.isDatepickerOpen = false;
      this.$emit('closed');
    },
    
    handleToggle(isOpen) {
      if (isOpen) {
        this.isDatepickerOpen = true;
        this.$emit('opened');
      } else {
        this.isDatepickerOpen = false;
        this.$emit('closed');
      }
    },
    
    showDatepicker() {
      if (this.$refs.hotelDatePicker && this.$refs.hotelDatePicker.showDatepicker) {
        this.$refs.hotelDatePicker.showDatepicker();
      }
      this.isDatepickerOpen = true;

      if (this.isPhone) {
        this.$nextTick(() => {
          this.$refs.hotelDatePicker.scrollToSelectedDates();
        });
      }
    },
    
    handleCloseButtonClick() {
      this.closeDatepicker(false); // Close without clearing selection
    },
    
    closeDatepicker(clearSelection = true) {
      if (this.$refs.hotelDatePicker && this.$refs.hotelDatePicker.hideDatepicker) {
        this.$refs.hotelDatePicker.hideDatepicker();
        if (clearSelection && !this.startDate && !this.endDate) {
          this.$refs.hotelDatePicker.clearSelection();
        }
      }
      this.isDatepickerOpen = false;
      this.$emit('datepickerClosed');
      this.enableBodyScroll();
    },
    
    // Alias for backward compatibility
    hideDatepicker() {
      this.closeDatepicker();
    },
    
    // Utility methods
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      const options = { year: "numeric", month: "numeric", day: "numeric" };
      return d.toLocaleString("fr", options);
    },
    
    formatDateKoedia(date) {
      if (!date) return '';
      if (date.includes('-')) return date;
      const dateBits = date.split('/');
      return `${dateBits[2]}-${dateBits[1]}-${dateBits[0]}`;
    },
    
    // Body scroll management for mobile
    disableBodyScroll() {
      if (typeof document !== 'undefined') {
        // Store the current scroll position before disabling scroll
        this.scrollPosition = window.scrollY;
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.top = `-${this.scrollPosition}px`;
      }
    },
    
    enableBodyScroll() {
      if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        // Restore the scroll position using the stored value
        if (this.scrollPosition > 0) {
          window.scrollTo(0, this.scrollPosition);
        }
      }
    },
    clearDates() {
      this.$refs.hotelDatePicker.clearSelection();
      this.$emit('periodSelected', null, null, null);
      this.$emit('datesCleared');
      if (this.showCloseButton && this.isPhone) {
        this.$nextTick(() => {
          if (this.isPhone) {
            this.showDatepicker(); // Keep datepicker open
          }
        });
      } else {
        if (this.autoHide) {
          this.isDatepickerOpen = false;
          this.closeDatepicker(false);
        }
      }
    },
    moveButtonToDatepicker() {
      if (this.isPhone) return;
      
      const button = this.$refs.clearDatesBtn;
      const datepicker = document.querySelector('.vhd__datepicker');
      
      if (button && datepicker && !datepicker.contains(button)) {
        datepicker.appendChild(button);
      }
    },
  },
  
  mounted() {
    if (this.isPhone) {
      this.i18n = {
          "day-names": ["D", "L", "M", "M", "J", "V", "S"],
          "month-names": [
            "Janvier",
            "Février",
            "Mars",
            "Avril",
            "Mai",
            "Juin",
            "Juillet",
            "Août",
            "Septembre",
            "Octobre",
            "Novembre",
            "Décembre",
          ],
          "check-in": "Arrivée",
          "check-out": "Départ",
          tooltip: {
          halfDayCheckIn: "Available CheckIn",
          halfDayCheckOut: "Available CheckOut",
          saturdayToSaturday: "Only Saturday to Saturday",
          sundayToSunday: "Only Sunday to Sunday",
          minimumRequiredPeriod: "%{minNightInPeriod} %{night} minimum.",
        },
      };
    }
    if (this.isPhone && this.showCloseButton) {
      // Add mobile-specific styling or behavior
      this.$nextTick(() => {
        const datepicker = this.$el.querySelector('.vhd__datepicker');
        if (datepicker) {
          datepicker.classList.add('mobile-datepicker');
        }
      });
    }
  },
  
  beforeUnmount() {
    // Ensure body scroll is restored when component is destroyed
    if (this.isPhone && this.isDatepickerOpen) {
      this.enableBodyScroll();
    }
  },
};
</script>

<style lang="scss" scoped>
.common-datepicker {
  position: relative;
  
  // Cross icon to close calendar (top right)
  @media screen and (max-width: 768px) {
    .close-cross-icon {
      position: fixed;
      top: 5px;
      right: 10px;
      width: auto;
      padding: 12px 10px;
      height: 32px;
      background: #3B6152;
      color: white;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      z-index: 1011;
    }
  }

  @media screen and (min-width: 769px) {
    .close-cross-icon {
      position: absolute;
      top: 30px;
      right: -10px;
      width: 32px;
      height: 32px;
      background: #3B6152;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      z-index: 1011;
      
      i {
        color: white;
      }
    }
  }
  
  &.mobile {
    .close-date-picker {
      position: fixed;
      bottom: 10px;
      left: 50%;
      transform: translateX(-50%);
      background-color: #3B6152;
      color: white;
      border: none;
      padding: 12px 24px;
      border-radius: 4px;
      font-family: 'Raleway', sans-serif;
      font-weight: 700;
      font-size: 14px;
      cursor: pointer;
      z-index: 1011;
      
      &:hover {
        background-color: #2d4a3d;
      }
    }
  }
}

.lodging-entire-datepicker {
  z-index: 0;
}
</style>


<style lang="scss">
@media screen and (max-width: 768px) {
  .common-datepicker.mobile {
    .vhd__datepicker__month {
      height: 33em !important;
    }
    .vhd__datepicker__header-mobile {
      display: none;
    }
  } 

  .vhd__datepicker__months {
    margin-top: 15px !important;
  }
}

.booking-form {
  .vhd__datepicker__inner {
    padding: 0 0 !important;
  }
  .vhd__datepicker__month-button {
    width: 1.5rem !important;
    height: 1.5rem !important;
  }
}

@media screen and (min-width: 769px) {
  .lodging-entire-datepicker {
    .vhd__datepicker__fullview {
      width: 70% !important;
    }
  }
}

.horizontal-search-bar {
  .common-datepicker {
    width: 35rem;

    .close-cross-icon {
      top: -8px;
    }
  }
}

@media screen and (min-width: 769px) {
  .clear-dates-button {
    position: absolute;
    bottom: 10px;
    left: 10px;
    color: #3B6152;
    cursor: pointer;
    z-index: 1001;
    transition: all 0.2s ease;
  } 
}


@media screen and (max-width: 768px) {
  .clear-dates-button {
    position: fixed;
    top: 5px;
    right: 85px;
    color: #3B6152;
    width: auto;
    height: 32px;
    cursor: pointer;
    z-index: 1011;
  }
}
.vhd__datepicker__month-day--halfCheckIn:before, .vhd__datepicker__month-day--halfCheckOut:before {
    border-bottom: 120px solid #e5e7eb !important;
    border-left: 120px solid transparent;
}
.vhd__datepicker__month-day--halfCheckOut:before {
    border-top: 120px solid #e5e7eb !important;
    border-bottom: unset !important;
    border-left: unset !important;
}
.vhd__datepicker__month-day--disabled:not(.checkIn):not(.checkOut) {
  background-color: #e5e7eb !important;
  color: #6b7280 !important;
}
</style>
